import { makeVar } from '@apollo/client'
import {
    BPStagedConfig,
    initialBPStagedConfig,
} from 'components/WorkspaceEditor/features/bestPractices/types/bpManagement.types'
import { canShowBestPracticeSetting } from 'shared/utils/bestPracticeUtils'

export interface WorkspaceToggles {
    areSharedPropertiesVisible: boolean
}

const workspaceTogglesInitial = {
    areSharedPropertiesVisible: true,
}

export const workspaceToggles = makeVar<WorkspaceToggles>({
    ...workspaceTogglesInitial,
})

export const setAreSharedPropertiesVisible = (value: boolean) => {
    workspaceToggles({ areSharedPropertiesVisible: value })
}

export const showBestPracticeSetting = makeVar<boolean>(false)

// PBI 2683633: BP on for every firm — only env-var rollout gate remains.
export const setShowBestPracticeSetting = () => {
    showBestPracticeSetting(canShowBestPracticeSetting())
}

// Monotonic counter — incremented by the apply hook to ask the right pane to
// open the BP Management tab. A counter (rather than a payload) keeps the
// effect a one-shot trigger: closing the pane manually does not retrigger it.
export const bpManagementOpenRequest = makeVar<number>(0)

export const requestBPManagementPanelOpen = () => {
    bpManagementOpenRequest(bpManagementOpenRequest() + 1)
}

// Per-contentItem refetch tick. Incremented by the apply hook after a
// successful Set/Modify/Remove so a mounted BestPracticesIconWrapper can
// refresh its details (the wrapper's useBestPracticeDetails hook uses
// no-cache imperative queries, so Apollo refetchQueries can't reach it).
export const bpDetailsRefetchTick = makeVar<Record<string, number>>({})

export const requestBPDetailsRefetch = (contentItemId: string) => {
    const current = bpDetailsRefetchTick()
    bpDetailsRefetchTick({
        ...current,
        [contentItemId]: (current[contentItemId] ?? 0) + 1,
    })
}

// Call this when a BestPracticesIconWrapper unmounts to prune its entry and
// prevent the map from growing unboundedly across the session.
export const clearBPDetailsRefetch = (contentItemId: string) => {
    const current = bpDetailsRefetchTick()
    if (!(contentItemId in current)) return
    const { [contentItemId]: _, ...rest } = current
    bpDetailsRefetchTick(rest)
}

export const bpStagedConfig = makeVar<BPStagedConfig>(initialBPStagedConfig)

export const setBPStagedConfig = (config: BPStagedConfig) => {
    bpStagedConfig(config)
}

export const resetBPStagedConfig = () => {
    bpStagedConfig(initialBPStagedConfig)
}

export interface BPRemoveConfirmationState {
    open: boolean
    resolve?: (confirmed: boolean) => void
}
export const bpRemoveConfirmation = makeVar<BPRemoveConfirmationState>({
    open: false,
})

// Promise-via-reactive-var bus: the apply hook awaits this; the dialog
// component renders on `open: true` and resolves on Yes/Cancel. If a previous
// request is still unresolved (e.g. caller fired twice rapidly), settle it as
// `false` first so the previous awaiter doesn't leak.
export const requestBPRemoveConfirmation = (): Promise<boolean> =>
    new Promise<boolean>((resolve) => {
        const previous = bpRemoveConfirmation()
        previous.resolve?.(false)
        bpRemoveConfirmation({ open: true, resolve })
    })

export const closeBPRemoveConfirmation = (confirmed: boolean) => {
    const current = bpRemoveConfirmation()
    current.resolve?.(confirmed)
    bpRemoveConfirmation({ open: false, resolve: undefined })
}
